// @noflow
import moment from 'moment';
import { SourceType } from 'Common/models/franchise';
import { isEmpty } from 'lodash';
import { setVideoProgress } from 'Common/cast/actions';
import notify from 'Common/utilities/reportingUtils';
import { getJson, postJson, putJson } from '../utilities/fetchUtils';
import type { Recent, PlayerCustomAttributes } from './types';

export const undockMiniPlayer = () => ({ dispatch }) =>
  dispatch({
    type: 'UNDOCK_MINI_PLAYER',
  });

export const dockMiniPlayer = () => ({ dispatch }) =>
  dispatch({
    type: 'DOCK_MINI_PLAYER',
  });

export const postToRecents = recentsInfo => ({ getState, dispatch, config }) => {
  const state = getState();
  const headers = {};
  const url = `${config.session.radish_url}recents`;

  if (!recentsInfo) {
    console.warn('postToRecents received invalid data');
    return Promise.resolve();
  }

  const data = {
    ...recentsInfo,
  };

  return postJson({ url, data, headers, state })
    .then(resp => {
      if (resp.status !== 'error') {
        return dispatch({
          type: 'POST_TO_RECENTS',
          payload: {
            ...resp,
            ...data,
          },
        });
      }
      return dispatch({
        type: 'POST_TO_RECENTS_ERROR',
        payload: {
          ...resp,
          data,
        },
      });
    })
    .catch(error => {
      console.warn('post to recents error...', error);
    });
};

export const putToRecents = (recentId, recentsInfo) => ({ getState, dispatch, config }) => {
  const state = getState();
  const headers = {};

  if (!recentId || !recentsInfo) {
    console.warn('putToRecents received invalid data');
    return Promise.resolve();
  }

  const data = {
    progress_seconds: Math.floor(recentsInfo.progressSeconds) || 0,
    total_seconds: Math.floor(recentsInfo.totalSeconds) || 0,
  };

  const url = `${config.session.radish_url}recents/${recentId}/progress`;

  return putJson({ url, data, headers, state })
    .then(resp => {
      if (resp.status === 'finished') {
        return dispatch({
          type: 'PUT_TO_RECENTS_FINISHED',
        });
      }
      if (resp.status !== 'error') {
        return dispatch({
          type: 'PUT_TO_RECENTS',
          payload: {
            ...resp,
            ...data,
          },
        });
      }
      return dispatch({
        type: 'PUT_TO_RECENTS_ERROR',
        payload: resp,
      });
    })
    .catch(error => {
      console.warn('put to recents error...', error);
    });
};

export const getProgressFromRecents = (contentId: string) => ({ getState, dispatch, config }) => {
  dispatch({
    type: 'REQUEST_RECENTS_PROGRESS',
  });

  const state = getState();
  const url = `${config.session.radish_url}recents?content_ids=${contentId}`;

  return getJson({ url, state })
    .then(response => {
      const progress = !isEmpty(response?.items) ? response.items[0].progress_seconds : 0;

      dispatch({
        type: 'RECEIVE_RECENTS_PROGRESS',
        progress,
      });
      return progress;
    })
    .catch(error => {
      console.warn('Error getting recent progress', error);
      dispatch({
        type: 'RECEIVE_RECENTS_PROGRESS',
        progress: 0,
      });
      return 0;
    });
};

export const getRecentsForFranchise = (
  franchiseSlug: string,
  callback: (Array<Recent>) => any,
) => ({ getState, dispatch, config }) => {
  dispatch({
    type: 'REQUEST_RECENTS_FOR_FRANCHISE',
  });

  const state = getState();
  const url = `${config.session.radish_url}recents`;

  return getJson({ url, state })
    .then(response => {
      const result = !isEmpty(response?.items)
        ? response.items.filter(item => item.franchise_slug === franchiseSlug)
        : [];

      callback(result);
      return dispatch({
        type: 'RECEIVE_RECENTS_FOR_FRANCHISE',
      });
    })
    .catch(error => {
      callback([]);
      console.warn('Error getting recent progress', error);
      return dispatch({
        type: 'RECEIVE_RECENTS_FOR_FRANCHISE',
        progress: 0,
      });
    });
};

export const fetchLiveVideoMetadata = (ottMappingId, platform = 'web', device = 'desktop') => ({
  getState,
  dispatch,
  config,
}) => {
  const state = getState();
  const headers = { Accept: 'application/vnd.echostar.franchise+json;version=1' };
  const url = `${
    config.session.radish_url
  }custom_attributes?ott_mapping_id=${ottMappingId}&platform=${platform}&device=${device}`;

  dispatch({ type: 'REQUEST_LIVE_VIDEO_METADATA', ottMappingId });

  return getJson({ url, headers, state, caching: { timeout: 120 } })
    .then(json =>
      dispatch({
        type: 'RECEIVE_LIVE_VIDEO_METADATA',
        payload: json,
        ottMappingId,
      }),
    )
    .catch(error =>
      dispatch({
        type: 'RECEIVE_LIVE_VIDEO_METADATA_ERROR',
        payload: error,
        ottMappingId,
      }),
    );
};

export const fetchVideoMetadata = (videoId, deviceType) => ({ getState, dispatch, config }) => {
  const state = getState();
  const headers = { Accept: 'application/vnd.echostar.franchise+json;version=1' };

  dispatch({
    type: 'REQUEST_VIDEO_METADATA',
    videoId,
  });

  const url = `${config.session.radish_url}v20/dol/video_player/metadata/${videoId}.json?platform=${
    deviceType ? 'mobile' : 'web'
  }&device_type=${deviceType || 'desktop'}`;

  return getJson({ url, headers, state, caching: { timeout: 120 } })
    .then(payload => {
      dispatch({
        type: 'RECEIVE_VIDEO_METADATA',
        videoId,
        payload,
      });
      return payload;
    })
    .catch(error => dispatch({ type: 'REQUEST_VIDEO_METADATA_ERROR', payload: error }));
};

export const fetchAiringsData = (networkSlug, airingType = 'linear') => ({
  getState,
  dispatch,
  config,
}) => {
  const headers = { Accept: 'application/vnd.echostar.channel_airings+json;version=1' };
  const state = getState();
  const { auth } = state;
  const { viewer } = auth;

  if (!viewer) {
    return Promise.resolve();
  }

  const { euid } = viewer;
  const startsBefore = moment()
    .add(1, 'hours')
    .toISOString();
  const endsAfter = moment()
    .add(1, 'minutes')
    .toISOString();

  const url = `${
    config.session.radish_url
  }channel_airings?starts_before=${startsBefore}&ends_after=${endsAfter}&airing_type=${airingType}&euid=${encodeURIComponent(
    euid || '',
  )}&service_ids=${encodeURIComponent(networkSlug)}&maxcount=100`;

  dispatch({
    type: 'REQUEST_AIRINGS_DATA',
    networkSlug,
    ottMappingId: networkSlug,
  });

  return getJson({ url, headers, state, caching: { timeout: 120 } })
    .then(payload =>
      dispatch({
        type: 'RECEIVE_AIRINGS_DATA',
        payload: payload.channel_airings[networkSlug],
        ottMappingId: networkSlug,
      }),
    )
    .catch(error => {
      console.warn(`failed to load airings for channel ${networkSlug}`);
      dispatch({ type: 'REQUEST_AIRINGS_DATA_ERROR', payload: error, ottMappingId: networkSlug });
    });
};

let qvtIntervalId;
/*
 * This boolean is used to discard a current QVT payload in case of the following sequence of events:
 * 1. REQUEST_QVT
 * 2. LL_EVENT_TRANSITION
 * 3. RECEIVE_QVT
 * In this case the payload will be for the previous QVT and not for the updated one,
 * that came with the LL event transition action.
 */
let discardNextQvtPayload;
let qvtCallNumber = 0;
let qvtErrorNumber = 0;

export const fetchQvt = (qvtUrl, next = false) => ({ getState, dispatch }) => {
  const state = getState();
  const {
    player: { sourceType },
  } = state;

  if (!qvtUrl || sourceType !== SourceType.LIVE_LINEAR) {
    clearInterval(qvtIntervalId);
    qvtIntervalId = undefined;
    qvtCallNumber = 0;
    qvtErrorNumber = 0;
    return Promise.resolve();
  }

  const headers = { Accept: 'application/vnd.echostar.franchise+json;version=1' };
  dispatch({ type: next ? 'REQUEST_NEXT_QVT' : 'REQUEST_QVT' });
  if (!next) {
    discardNextQvtPayload = false;
  }
  try {
    qvtCallNumber += 1;
    return getJson({ url: qvtUrl, headers, state, withResponseHeaders: true }).then(payload => {
      if (!discardNextQvtPayload || next) {
        dispatch({
          type: next ? 'RECEIVE_NEXT_QVT' : 'RECEIVE_QVT',
          payload,
        });
        if (next) {
          return;
        }
      } else if (!next) {
        console.log('LL event transition intervened! Discarding this QVT response');
      }
      if (qvtIntervalId === undefined) {
        qvtIntervalId = setInterval(
          () => {
            const {
              player: {
                sourceType,
                qvtPayload: { self },
              },
            } = getState();
            if (sourceType === SourceType.LIVE_LINEAR) {
              dispatch(fetchQvt(self, false));
            }
          },
          /* Reduce amount of requests for CC mode (chromcecast enabled devices aren't performant). */
          process.env.IN_CHROMECAST_MODE ? 5 * 1000 : 1000,
        );
      }
    });
  } catch (error) {
    qvtErrorNumber += 1;
    if (qvtErrorNumber === 1) {
      notify({ error, metaData: { qvtCallNumber } });
    }
    console.error(error);
  }
};

export const clearQvtInterval = () => ({ dispatch }) => {
  if (qvtIntervalId) {
    clearInterval(qvtIntervalId);
    qvtIntervalId = undefined;
  }
  return dispatch({ type: 'CLEAR_QVT_INTERVAL' });
};

export const llEventTransition = () => ({ dispatch }) => {
  discardNextQvtPayload = true;
  dispatch({
    type: 'LL_EVENT_TRANSITION',
  });
};

export const setPlayerStream = ({
  stream,
  playerType = 'nosling',
  sourceType = SourceType.ON_DEMAND,
  playerVisible = true,
  contentId,
  listing,
  channelInformation,
  analyticsData,
}) => ({ dispatch }) => {
  dispatch({
    type: 'SET_PLAYER_STREAM',
    stream,
    playerVisible,
    playerType,
    sourceType,
    contentId,
    listing,
    channelInformation,
    analyticsData,
  });

  const qvtUrl = stream.qvtUrl || stream.qvt_url;

  if (sourceType === SourceType.LIVE_LINEAR && qvtUrl) {
    dispatch(fetchQvt(qvtUrl));
  }
};

export const resetDVRStatus = () => ({ dispatch }) => {
  dispatch({
    type: 'RESET_DVR_STATUS',
  });
};

export const resetPlayer = playerVisible => ({ dispatch }) => {
  if (qvtIntervalId) {
    clearInterval(qvtIntervalId);
    qvtIntervalId = undefined;
  }
  dispatch({
    type: 'RESET_PLAYER',
    playerVisible,
  });
};

export const playerStreamLoaded = () => ({ dispatch }) => {
  dispatch({
    type: 'PLAYER_STREAM_LOADED',
  });
};

export const setDVRStream = ({
  dvrId,
  dvrListing,
  dvrProgress = 0,
  playerVisible = true,
  contentId,
  resumePlayback,
}) => ({ dispatch }) => {
  dispatch({
    type: 'SET_DVR_STREAM',
    dvrId, // pgm_id
    dvrListing, // DvrEventListing,
    dvrProgress, // number
    playerVisible,
    playerType: 'sling',
    sourceType: SourceType.SLING_DVR,
    contentId,
    resumePlayback,
  });
};

export const setLiveStream = (channel, playerVisible = true, listing) => ({ dispatch }) =>
  Promise.resolve(
    dispatch({
      type: 'SET_LIVE_STREAM',
      channel,
      listing,
      playerVisible,
      playerType: 'sling',
      sourceType: SourceType.SLING_LIVE,
    }),
  );

export const setSlingPlaybackInfo = (slingPlaybackInfo, isSeeking = false) => ({
  dispatch,
  getState,
}) => {
  const store = getState();
  const { contentType, currentTime, duration } = store.player.slingPlaybackInfo;

  if (slingPlaybackInfo) {
    const {
      contentType: nextContentType,
      currentTime: nextCurrentTime,
      duration: nextDuration,
    } = slingPlaybackInfo;

    if (
      typeof nextDuration !== 'number' ||
      typeof nextCurrentTime !== 'number' ||
      nextContentType === 'none'
    ) {
      return;
    }

    if (
      contentType !== nextContentType ||
      currentTime !== nextCurrentTime ||
      duration !== nextDuration
    ) {
      dispatch({
        type: 'SET_SLING_PLAYBACK_INFO',
        isSeeking,
        slingPlaybackInfo,
      });
      if (store.config.cast.enabled && store.player.sourceType === SourceType.SLING_DVR) {
        const progress = Math.floor(nextCurrentTime / 1000);
        dispatch(setVideoProgress(progress));
      }
    }
  }
};

export const togglePlayerContainer = containerOpen => ({ dispatch }) =>
  dispatch({
    type: 'TOGGLE_PLAYER_CONTAINER',
    containerOpen,
  });

export const togglePlayerVisibility = playerVisible => ({ dispatch }) =>
  dispatch({
    type: 'TOGGLE_PLAYER_VISIBILITY',
    playerVisible,
  });

export const toggleUnsupportedReceiverModal = (modalOpen, reason) => ({ dispatch }) =>
  dispatch({
    type: 'TOGGLE_UNSUPPORTED_RECEIVER_MODAL',
    modalOpen,
    reason,
  });

export const setPlaybackCouldNotBeStarted = playbackCouldNotBeStarted => ({ dispatch }) =>
  dispatch({
    type: 'PLAYBACK_COULD_NOT_BE_STARTED',
    playbackCouldNotBeStarted,
  });

export const setPlayerProgramInfo = (programInfo, contentType = '') => ({ dispatch }) =>
  dispatch({
    type: 'SET_PLAYER_PROGRAM_INFO',
    programInfo,
    contentType,
  });

/**
 * Start conncting as soon as slingplayer is initialized
 */
export const slingPlayerConnect = () => ({ dispatch }) =>
  dispatch({
    type: 'SLING_PLAYER_CONNECT',
  });

/**
 * Start playing as soon as slingplayer is conencted
 */
export const slingPlayerPlay = () => ({ dispatch }) => {
  dispatch({
    type: 'SLING_PLAYER_PLAY',
  });
};

export const slingPlayerStop = () => ({ getState, dispatch }) => {
  dispatch({
    type: 'SLING_PLAYER_STOP',
  });
  const state = getState();
  const activeReceiverId = state.app.activeReceiver?.receiverId;
  const receivers = state.auth.viewer ? state.auth.viewer.receivers : [];
  dispatch({
    type: 'RATING_DIALOG_TRIGGER',
    activeReceiverId,
    receivers,
  });
};

export const setSlingPlayerError = (errorReason, errorMessage, errorType, error, errorCode) => ({
  dispatch,
}) => {
  dispatch({
    type: 'SLING_PLAYER_ERROR',
    errorReason,
    errorMessage,
    errorType,
    error,
    errorCode,
  });
};

export const setSlingPlayerLoading = (loading, status) => ({ dispatch }) =>
  dispatch({
    type: 'SET_SLING_PLAYER_LOADING',
    loading,
    status,
  });

export const setSlingPlayerStatus = status => ({ dispatch }) => {
  dispatch({
    type: 'SET_SLING_PLAYER_STATUS',
    status,
  });
};

export const setSlingPlayerConnectionMode = mode => ({ dispatch }) =>
  dispatch({
    type: 'SET_SLING_PLAYER_CONNECTION_MODE',
    mode,
  });

export const toggleDebugInfo = visible => ({ dispatch }) =>
  dispatch({
    type: 'TOGGLE_DEBUG_INFO',
    debugVisible: visible,
  });

export const dismissAsyncPopup = () => ({ dispatch, getState }) => {
  const state = getState();
  if (state.player.asyncPopupInfo) {
    dispatch({
      type: 'DISMISS_ASYNC_POPUP',
    });
  }
};

export const showAsyncPopup = data => ({ dispatch }) =>
  dispatch({
    type: 'SHOW_ASYNC_POPUP',
    data,
  });

export const dismissTunersPopup = () => ({ dispatch }) =>
  dispatch({
    type: 'DISMISS_TUNERS_POPUP',
  });

export const showTunersPopup = data => ({ dispatch }) =>
  dispatch({
    type: 'SHOW_TUNERS_POPUP',
    data,
  });

export const switchPlayerData = ottMappingId => ({ dispatch }) =>
  Promise.resolve(
    dispatch({
      type: 'SWITCH_PLAYER_DATA',
      ottMappingId,
    }),
  );

export const nativePlayerToFullscreen = toFullscreen => ({
  type: toFullscreen ? 'NATIVE_PLAYER_IS_FULLSCREEN' : 'NATIVE_PLAYER_IS_COLLAPSED',
});

export const resetPlayerStream = () => ({ dispatch }) =>
  dispatch({
    type: 'RESET_PLAYER_STREAM',
  });

export const startPlaybackInProgress = () => ({ dispatch }) =>
  dispatch({
    type: 'START_PLAYBACK_IN_PROGRESS',
  });

export const startPlaybackFromBeginning = () => ({ dispatch }) => {
  dispatch({
    type: 'START_PLAYBACK_FROM_BEGINNING',
  });
};

export const setPlayerError = (
  code,
  responseCode,
  name,
  errorType,
  responseText,
  serverResponse,
) => ({ dispatch }) =>
  dispatch({
    type: 'PLAYER_ERROR',
    code,
    responseCode,
    name,
    errorType,
    responseText,
    serverResponse,
  });

export const showBingeMessage = (timeLeft, nextEpisode) => ({ dispatch }) =>
  dispatch({
    type: 'SHOW_BINGE_MESSAGE',
    timeLeft,
    nextEpisode,
  });

export const isNextBingeEpisodeLoading = isNextBingeEpisodeLoading => ({ dispatch }) =>
  dispatch({
    type: 'IS_NEXT_BINGE_EPISODE_LOADING',
    isNextBingeEpisodeLoading,
  });

export const cancelNextBingeEpisode = clearData => ({ dispatch }) =>
  dispatch({
    type: 'CANCEL_NEXT_BINGE_EPISODE',
    clearData,
  });

export const setSlingErrorsObject = ({ errorCode, slingError }) => ({ dispatch }) =>
  dispatch({
    type: 'SET_SLING_ERRORS_OBJECT',
    slingError,
    errorCode,
  });

export const resetSlingErrorsObject = () => ({ dispatch }) =>
  dispatch({
    type: 'RESET_SLING_ERRORS_OBJECT',
  });

export const setPlayerEventObject = data => ({ dispatch }) =>
  dispatch({
    type: 'SET_PLAYER_EVENT_OBJECT',
    data,
  });

export const setSlingPlayerEventObject = data => ({ dispatch }) =>
  dispatch({
    type: 'SET_SLING_PLAYER_EVENT_OBJECT',
    data,
  });

export const setPlayerSessionActive = isActive => ({ dispatch }) =>
  dispatch({
    type: 'SET_PLAYER_SESSION_ACTIVE',
    isActive,
  });

export const setSlingPlayerLocalStorageItem = (key: string, value: string) => ({
  type: 'SET_SLING_PLAYER_LOCAL_STORAGE_ITEM',
  key,
  value,
});

export const removeSlingPlayerLocalStorageItem = (key: string) => ({
  type: 'REMOVE_SLING_PLAYER_LOCAL_STORAGE_ITEM',
  key,
});

export const clearSlingPlayerLocalStorage = () => ({
  type: 'CLEAR_SLING_PLAYER_LOCAL_STORAGE',
});

export const setIsAdPlaying = (isAdPlaying: boolean) => ({
  type: 'IS_AD_PLAYING',
  isAdPlaying,
});

export const setPlayerLastActive = lastActiveTimestamp => ({ dispatch }) =>
  dispatch({
    type: 'SET_PLAYER_LAST_ACTIVE',
    playerLastActive: lastActiveTimestamp,
  });

export const setPlayerPageOpened = (playerPageOpened: boolean) => ({
  type: 'SET_PLAYER_PAGE_OPENED',
  playerPageOpened,
});

export const setDebugModeEnabled = (enabled: boolean) => ({
  type: 'SET_DEBUG_MODE_ENABLED',
  enabled,
});

export const fetchPlayerCustomAttributes = () => ({ getState, dispatch, config }) => {
  const state = getState();
  const url = `${config.session.radish_url}v20/dol/video_player/metadata/0.json`;

  return getJson({ url, state, caching: { timeout: 120 } }).then(
    ({ custom_attributes: customPlayerAttributes = {} }) => {
      const customAttributes: PlayerCustomAttributes = {
        convivaServiceUrl: customPlayerAttributes.conviva_service_url,
        convivaCustomerKey: customPlayerAttributes.conviva_customer_key,
        convivaEnabled: customPlayerAttributes.conviva_enabled,
      };
      dispatch({
        type: 'RECEIVE_PLAYER_ATTRIBUTES',
        customAttributes,
      });
    },
  );
};

export const setBufferLevel = (bufferLevel: number) => ({
  type: 'SET_BUFFER_LEVEL',
  bufferLevel,
});
